import { useQuery } from '@tanstack/react-query';

import { DDComponent } from '@telialt/utils/src/datadog/setup';
import { useLogError } from '@telialt/utils/src/datadog/useLogError';
import { useHybrisSegment } from '@telialt/utils/src/segment';

import { SEARCH_QUERY_KEYS, SEARCH_QUERY_MIN_LENGTH } from '../constants';
import { loadAutoCompleteSuggestions } from './DataLoading/LoadAutoCompleteSuggestions';
import { loadContentSuggestions } from './DataLoading/LoadContentSuggestions';
import { loadHelpSuggestions } from './DataLoading/LoadHelpSuggestions';
import { loadProductsSuggestions } from './DataLoading/LoadProductsSuggestions';
import { loadServicesSuggestions } from './DataLoading/LoadServicesSuggestions';
import { useLoadQueryKeys } from './useLoadQueryKeys';

export const useLoadSearchSuggestions = (keyword: string) => {
    const searchText = keyword.length < SEARCH_QUERY_MIN_LENGTH ? '' : keyword;

    const { isLoading: isQueryKeysLoading, queryKeys } = useLoadQueryKeys();

    const segment = useHybrisSegment();
    const isB2cOrB2b = segment === 'b2c' || segment === 'b2b';

    const {
        data,
        isLoading: isSuggestionsLoading,
        isFetching: isSuggestionsFetching,
        error,
    } = useQuery({
        queryKey: [SEARCH_QUERY_KEYS.SEARCH_SUGGESTION, segment, searchText],
        queryFn: async ({ signal }) => {
            const [
                { suggestions, error: suggestionsError },
                { products, error: productsError },
                { services, error: servicesError },
                { help, error: helpError },
                { content, error: contentError },
            ] = await Promise.all([
                loadAutoCompleteSuggestions({ searchText, segment, queryKeys, signal }),
                isB2cOrB2b
                    ? loadProductsSuggestions({ searchText, segment, queryKeys, signal })
                    : { products: undefined, error: undefined },
                loadServicesSuggestions({ searchText, segment, queryKeys, signal }),
                !!searchText && isB2cOrB2b
                    ? loadHelpSuggestions({ searchText, segment, queryKeys, signal })
                    : { help: undefined, error: undefined },
                !!searchText
                    ? loadContentSuggestions({ searchText, segment, queryKeys, signal })
                    : { content: undefined, error: undefined },
            ]);

            if (suggestionsError && productsError && servicesError && helpError && contentError) {
                throw suggestionsError;
            }

            return { suggestions, products, services, help, content, searchText };
        },
        enabled: !!queryKeys,
        staleTime: 5000,
    });
    useLogError(error, DDComponent.Search);

    return {
        isLoading: isQueryKeysLoading || isSuggestionsLoading,
        isFetching: isSuggestionsFetching,
        error,
        suggestions: data?.suggestions,
        products: data?.products,
        services: data?.services,
        help: data?.help,
        content: data?.content,
        searchText: data?.searchText,
    };
};
